import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { LicenseArea } from 'src/app/data-models/area-models/license-area.model';
import { Logbook } from 'src/app/data-models/user-models/logbook.model';
import { CatchService } from 'src/app/services/reporting/catch.service';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { VesselData } from '../../data-models/user-models/vessel-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { UserStorageService } from '../../services/storage/user-storage.service';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

@Component({
    selector: 'app-records-page',
    templateUrl: './records-page.component.html',
    styleUrls: ['./records-page.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})

export class RecordsPageComponent {

  // start reports are deliberately absent: a start report carries the trip's FIN, logbook and
  // license area, and rewriting those after the fact would re-file every other report on the trip
  private reportRouteMap: Record<string, string> = {
    gillnet: 'gill-net-report',
    seine: 'seine-report',
    troll: 'troll-report',
    end: 'end-report',
    offload: 'offload-report'
  };

  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered

  //data preambles
  userDetails: UserData = new UserData();
  adminUser?: UserData;
  currTrip: Trip = new Trip();
  prevTrips: Trip[] = [];
  unsubmittedTrips: Trip[] = []; // trips that have unsubmitted reports
  numUnsubmittedReports: number = 0; // the user's total number of unsubmitted reports

  //filter menu
  logbooks: Logbook[] = [];
  licenseAreas: LicenseArea[] = [];
  vessels: VesselData[] = [];
  selectedLogbookCount: number = 0;
  selectedLicenseAreaCount: number = 0;
  selectedVesselCount: number = 0;
  selectedDateRange: string = "";

  // Form Control 
  tripDateForm: UntypedFormGroup = new UntypedFormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null)
  });

  // get the needed data from the user pouchdb and common couchdb
  constructor(
    private authenticationService: AuthenticationService,
    private ref: ChangeDetectorRef,
    private onlineStatusService: OnlineStatusService,
    public commonDataService: CommonDataService,
    public submissionService: SubmissionService,
    public sharedMethodsService: SharedMethodsService,
    public catchService: CatchService,
    private dialog: MatDialog,
    private router: Router,
  
  ) {

    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
      this.handleOnlineStatus();
    } else {
      this.initializeUserAndTripData();
    }
  }


  private handleOnlineStatus() {

    UserStorageService.updateConfirmationNumbers().then((success: boolean) => {
      if (success) {
        this.sharedMethodsService.toastTripDataSynced();
      } else {
        this.sharedMethodsService.toastSessionExpired();
      }

      this.initializeUserAndTripData();
    });

  }


  /*  ---------- Page Initialization Methods  ---------- */


  private initializeUserAndTripData() {

    let requiredData: string[] = ['license_areas_list']; // array of documents needed from the common data pouch for this component

    UserStorageService.getCurrTrip().then(() => {
      if (this.authenticationService.dataError(UserStorageService.error)) return;
      this.currTrip = UserStorageService.currTrip;
      console.log(this.currTrip)
    }).then(() => {
      UserStorageService.getAllPrevTrips().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.prevTrips = UserStorageService.prevTrips.slice();
        this.prevTrips.sort((a, b) =>
          b.getStartDate().getTime() - a.getStartDate().getTime()
        );
        this.commonDataService.getSpecifiedDocs(this.adminUser?.userEmail ?? this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
        this.userDetails = UserStorageService.currUser;
        this.adminUser = UserStorageService.adminUser;
      });
    });

  }


  initialize(): void {
    
    this.makeUnsubmittedTripList();

    // get data for filter list
    this.logbooks = this.userDetails.logbooks;
    this.logbooks.forEach(lb => lb.isChecked = false);
    this.updateSelectedItems(); 
    this.makeLicenseAreaList(CommonDataService.licenseAreas, this.userDetails.logbooks);
    this.vessels = this.userDetails.vessels;

    this.isDataLoaded = true; // set flag to render HTML
    this.ref.detectChanges(); // re-render HTML

  }

  // create the list of license areas shown in the filter menu
  makeLicenseAreaList(areas: LicenseArea[], logbooks: Logbook[]): void {

    for (let area of areas) {
      for (let logbook of logbooks) {
        if (area.gearType === logbook.gearName) {
          this.licenseAreas.push(new LicenseArea(area.licenseName, area.licenseLocation));
          break;
        }
      }
    }

  }


  // creates a list of all trips with unsubmitted reports (including the current trip)
  // as well as creates a tally of the total number unsubmitted reports
  makeUnsubmittedTripList(): void {

    if (this.currTrip.unsubmittedReports.length) {
      this.numUnsubmittedReports += this.currTrip.unsubmittedReports.length;
      this.unsubmittedTrips.push(this.currTrip);
    }

    for (let trip of this.prevTrips) {
      if (trip.unsubmittedReports.length) {
        this.numUnsubmittedReports += trip.unsubmittedReports.length;
        this.unsubmittedTrips.push(trip);
      }
    }

  }


  /*  ---------- Unsubmitted Report Methods  ---------- */


  // attempts to send all outstanding reports to DFO - invoked by the user
  attemptSendReports(): void {

    this.isDataLoaded = false;

    this.submissionService.reSendReports(this.unsubmittedTrips, this.numUnsubmittedReports).then((trips: Trip[]) => {
      this.updateTrips(trips).then(() => { // update the trips in the user's db with the returned trips
        this.sharedMethodsService.reloadWindow(); // reload the page to display the updated data
      })
    });

  }

  // updates the trips in the user's pouch db with the trips returned from reSendReports() 
  // The trips will have updated unsubmittedReport arrays and contain reports with updated isSubmitted values
  async updateTrips(updatedTrips: Trip[]): Promise<void> {

    // check if the current trip has been updated
    for (let updatedTrip of updatedTrips) {
      if (this.currTrip.docID === updatedTrip.docID) {
        await UserStorageService.saveTrip(updatedTrip);
      }
    }

    // check if any past trips have been updated
    for (let trip of this.prevTrips) {
      for (let updatedTrip of updatedTrips) {
        if (trip.docID === updatedTrip.docID) {
          await UserStorageService.saveTrip(updatedTrip, false);
        }
      }
    }

  }

  // determines if an array of reports (ex. pause, offload, catch) contains any that are unsubmitted
  containsUnsubmittedReports(reportArray: any): boolean {

    for (let report of reportArray) {
      if (!report.isSubmitted) {
        return true; // return true if there is an unsubmitted report
      }
    }
    return false;

  }


  /*  ---------- General Filter Tool Methods  ---------- */


  // examines selected filter criteria to determine whether a trip remains visible
  determineTripVisibility(trip: Trip): boolean {

    let checkedLogbooks: Logbook[] = this.logbooks.filter((g: Logbook) => g.isChecked);
    let checkedAreas: LicenseArea[] = this.licenseAreas.filter((l: LicenseArea) => l.isChecked);
    let checkedVessels: VesselData[] = this.vessels.filter((v: VesselData) => v.isChecked);

    let logbookFilter: boolean = checkedLogbooks.length == 0 ? true : this.filterLogbooks(checkedLogbooks, trip);
    let areaFilter: boolean = checkedAreas.length == 0 ? true : this.filterAreas(checkedAreas, trip);
    let vesselFilter: boolean = this.filterVessels(checkedVessels, trip);
    let dateFilter: boolean = this.filterDate(trip);

    return logbookFilter && areaFilter && vesselFilter && dateFilter;
  
  }

  /* SelectItem() Credit to: 
  https://stackblitz.com/edit/multi-select-mat-menu?file=src%2Fapp%2Fmenu-multiselect-example.html,src%2Fapp%2Fmenu-multiselect-example.ts
  for selectItem and updateSelectedItems */
 
  selectItem($event: any, item: any): void {

    // prevent menu from closing
    $event.stopPropagation();
    $event.preventDefault();
    // toggle selected state on clicked item
    item.isChecked = !item.isChecked;
    this.updateSelectedItems();

  }

  determineButtonStyling(): string {

    if (this.filtersApplied()) { // filter is being used
      return 'filter-tool-selected';
    }
    return 'filter-tool'; // filter is not being used
  }

  filtersApplied(): boolean {
    return this.selectedLogbookCount > 0 || this.selectedLicenseAreaCount > 0 || this.selectedVesselCount > 0 || this.isFilled();
  }


  /*  ---------- Date Filter Methods  ---------- */


  isFilled(): boolean {
    if ((this.tripDateForm.get('startDate')?.value) == null &&
      (this.tripDateForm.get('endDate')?.value) == null) {
      return false;
    }
    return true;
  }

  clearForm(): void {
    this.tripDateForm.controls['startDate'].setValue(null);
    this.tripDateForm.controls['endDate'].setValue(null);
  }

  filterDate(trip: Trip): boolean {
    // form inputs
    let startDateForm: Date = (this.tripDateForm.get('startDate')?.value);
    let endDateForm: Date = (this.tripDateForm.get('endDate')?.value);
    
    // trip start/end dates
    let tripStart: Date = new Date(trip.getStartDate().toDateString());
    let tripEnd: Date = new Date(trip.getEndDate().toDateString());

    //no selection has been made
    if (!startDateForm && !endDateForm) {
      return true;
      // one date or two of the same day have been selected
    } else if ((!startDateForm || !endDateForm) || (startDateForm.toDateString() == endDateForm.toDateString())) {
      startDateForm = startDateForm ?? endDateForm;
      endDateForm = endDateForm ?? startDateForm;
      startDateForm = new Date(startDateForm.toDateString());
      endDateForm = new Date(endDateForm.toDateString());
      
      this.selectedDateRange = this.dateRangeString(startDateForm, endDateForm);
      return (tripStart <= startDateForm && endDateForm <= tripEnd);
      // date range has been selected
    } else {
      startDateForm.setHours(0, 0, 0, 0);
      endDateForm.setHours(0, 0, 0, 0);
      this.selectedDateRange = this.dateRangeString(startDateForm, endDateForm);
      return ((startDateForm <= tripStart && tripEnd <= endDateForm) || // inclusive 
        (tripStart <= startDateForm && startDateForm <= tripEnd) ||
        (tripStart <= endDateForm && endDateForm <= tripEnd));
    }
  }
    
  dateRangeString(date1: Date, date2: Date): string {
    let str: string = date1.getDate() + "/" + (date1.getMonth() + 1) + "/" + date1.getFullYear() + " - " +
      date2.getDate() + "/" + (date2.getMonth() + 1) + "/" + date2.getFullYear();
    return str;
  }


  /*  ---------- Logbook, Vessel, & License Area Filter Methods  ---------- */


  updateSelectedItems(): void {

    this.selectedLogbookCount = this.logbooks.filter((g: Logbook) => g.isChecked).length;
    this.selectedLicenseAreaCount = this.licenseAreas.filter((l: LicenseArea) => l.isChecked).length;
    this.selectedVesselCount = this.vessels.filter((v: VesselData) => v.isChecked).length;

  }

  filterLogbooks(checkedLogbooks: Logbook[], trip: Trip): boolean {
    return (!checkedLogbooks.length ? this.logbooks : checkedLogbooks).some((g: Logbook) => trip.logbook.logbookID === g.logbookID);
  }

  filterAreas(checkedAreas: LicenseArea[], trip: Trip): boolean {
    return (!checkedAreas.length ? this.licenseAreas : checkedAreas).some((l: LicenseArea) => trip.licenseArea.licenseName === l.licenseName);
  }

  filterVessels(checkedVessels: VesselData[], trip: Trip): boolean {
    return (!checkedVessels.length ? this.vessels : checkedVessels).some((v: VesselData) => trip.vessel.vesselRegistrationNumber === v.vesselRegistrationNumber);
  }


  clearLogbookFilter(): void {
    this.selectedLogbookCount = 0;
    this.logbooks.forEach((g: Logbook) => g.isChecked);   
  }

  clearAreaFilter(): void {
    this.selectedLicenseAreaCount = 0;
    this.licenseAreas.forEach((l: LicenseArea) => l.isChecked);
  }

  clearVesselFilter(): void {
    this.selectedVesselCount = 0;
    this.vessels.forEach((v: VesselData) => v.isChecked = false);
  }


  /*  ---------- Misc Methods  ---------- */


  // returns the appropriate table columns for the offload catch table (dependant on gear type)
  determineOffloadColumns(currTrip: string): string[] {
    return currTrip === 'Seine' ? this.catchService.seineOffloadColumns : this.catchService.gillNetOffloadColumns;
  }


  updateConfirmationNumbers() {

    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
      this.sharedMethodsService.reloadWindow();
    } else {
      this.sharedMethodsService.toastOfflineSyncError();
    }

  }

  /*  ---------- Trip Switching Methods  ---------- */


  // A past trip can be resumed only while its own logbook is still registered and active. Mirrors
  // the eligibility the start report applies when it offers logbooks: reports filed against a
  // logbook that has been deactivated (season rollover, deactivateSeasonLogbooks) are rejected by
  // FOS, so resuming such a trip could only produce errors. Admins are read-only -- saveUser
  // refuses to write while an admin is impersonating, so the switch could not be completed anyway.
  canResumeTrip(trip: Trip): boolean {

    if (this.adminUser) return false;

    return this.userDetails.logbooks.some((logbook: Logbook) =>
      logbook.logbookID === trip.logbook.logbookID && logbook.isActive && logbook.isRegistered);

  }

  // Makes a past trip the current one again.
  //
  // Both halves of the state have to move together: setActiveTrip flips isActive on the trip
  // documents, and currHomepage is what actually lets the user reach the trip -- the route guard
  // authorizes every report route off it and logs the user out when it says no, and this page
  // shows its own "Current Trip" section on the same field. The trip flag is written first so the
  // active-trip homepage finds the trip already active when it loads.
  //
  // setActiveTrip's return value is deliberately ignored, as it is at every other saveTrip call
  // site: false means the Cloudant push failed, not that the switch failed -- the local write has
  // already happened and offline-at-sea is the normal case. Surfacing it as an error would tell the
  // user the switch did not happen when it did.
  onResumeTrip(trip: Trip): void {

    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'resume_trip' }
    })
      .afterClosed()
      .subscribe(ok => {
        if (!ok) return;

        this.isDataLoaded = false;
        this.ref.detectChanges();

        UserStorageService.setActiveTrip(trip).then(() => {
          this.userDetails.currHomepage = 'active-trip-home';
          UserStorageService.saveUser(this.userDetails).then(() => {
            this.router.navigate([this.userDetails.currHomepage]);
          });
        });
      });

  }


  // opens the edit report dialog
  onEditReport(
    trip: Trip,
    type: 'gillnet' | 'seine' | 'troll' | 'end' | 'offload',
    internalId: string
  ) {
    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'edit_report' }
    })
      .afterClosed()
      .subscribe(ok => {
        if (!ok) return;

        // grab the real report instance
        let report: any;
        switch (type) {
          case 'end':
            report = trip.endFishingReport;
            break;

          case 'offload':
            report = trip.offloadReports.find(r => r.internalId === internalId)!;
            break;

          case 'gillnet':
          case 'seine':
          case 'troll':
            report = trip.dailyCatchReports.find(r => r.internalId === internalId)!;
            break;
        }
        console.log ('Type: ', type, 'Report: ', report);
        // navigate, passing the original trip + report into the report component
        this.router.navigate(
          [this.reportRouteMap[type], internalId],
          {
            queryParams: { status: 'post_submission_edit' },
            state: { trip, report }
          }
        );
      });
  }

}
